import { MAXIMUM_SPACES_PER_BOOKING } from 'constants/bookings';
import { Form, FormikConfig, FormikProvider, FormikValues } from 'formik';

import useAvailability from 'hooks/useAvailability';
import useBookingItems from 'hooks/useBookingItems';
import useBookingSlots from 'hooks/useBookingSlots';
import useFormikBooking from 'hooks/useFormikBooking';
import useVenueOpeningTimes from 'hooks/useVenueOpeningTimes';
import { ComponentProps, FC, useContext, useMemo, useState } from 'react';
import { AuthContext } from 'state/auth';

import { getCalendarAvailability } from 'utils/booking';
import Calendar from '../calendar';
import SpaceListModals, { ModalType } from '../spaceList/modals';
import Header from './header';
import SpaceDetails from './spaceDetails';

interface Space {
  id: number;
  name: string;
  clinical: boolean;
}

interface Venue {
  id: number;
  spaces?: Space[];
}

interface Props {
  date: string;
  setDate?: (date: string) => void;
  venue: Venue;
  spaces?: Space[];
  spaceTypeValid?: (space: Space) => boolean;
  spaceServiceClash?: (space: Space) => boolean;
  limitSpaceSelections?: boolean;
  initialValues?: object;
}

const compareSpaces = (a: Space, b: Space) => {
  const clinical = (a.clinical ? 0 : 1) - (b.clinical ? 0 : 1);
  return clinical === 0 ? a.name.localeCompare(b.name) : clinical;
};

const noOp = () => null;
const emptyList: Space[] = [];

const VenueCalendar: FC<Props> = ({
  date,
  setDate = noOp,
  venue = {} as Venue,
  spaces = venue.spaces || emptyList,
  spaceTypeValid = () => true,
  spaceServiceClash = () => false,
  limitSpaceSelections = true,
  initialValues,
  ...props
}) => {
  const { formik, combinedSlots } = useFormikBooking(true, {
    date,
    ...initialValues,
  } as unknown as FormikConfig<FormikValues>);

  const { values, setFieldValue } = formik;

  const {
    data: { openingTime, closingTime },
    loading: openingTimesLoading,
  } = useVenueOpeningTimes(date, venue.id);

  const { data: bookingItems, loading: bookingItemsLoading } = useBookingItems(
    date,
    spaces.map(({ id }) => id)
  );

  const { data: bookingSlots, loading: bookingSlotsLoading } = useBookingSlots(
    date,
    venue.id
  );

  const selectedSpaces = useMemo(
    () => spaces.filter(({ id }) => values.spaceIds.includes(id)),
    [spaces, values.spaceIds]
  );

  const clinical = useMemo(
    () => selectedSpaces.some(({ clinical }) => clinical) || undefined,
    [selectedSpaces]
  );

  const { data: availability } = useAvailability(
    date as unknown as Date,
    values.service,
    selectedSpaces as unknown as Parameters<typeof useAvailability>[2],
    combinedSlots,
    undefined,
    undefined,
    values.id ? Number(values.id) : undefined
  );

  const loading =
    openingTimesLoading || bookingItemsLoading || bookingSlotsLoading;

  const { loggedIn, accountApproved } = useContext(AuthContext);

  const [openModal, setOpenModal] = useState<ModalType | null>(null);
  const closeModal = () => setOpenModal(null);

  const addToBooking = (
    space: Space,
    venue: Venue,
    serviceClash: boolean,
    typeValid: boolean
  ) => {
    if (!typeValid) {
      return setOpenModal(
        space.clinical
          ? 'space-type-clinical-error'
          : 'space-type-non-clinical-error'
      );
    }

    if (serviceClash) {
      return setOpenModal('service-clash');
    }

    if (!loggedIn) {
      return setOpenModal('sign-in');
    }
    if (!accountApproved) {
      return setOpenModal('unapproved-user-warning');
    }

    if (
      limitSpaceSelections &&
      values.spaceIds.length === MAXIMUM_SPACES_PER_BOOKING
    ) {
      return setOpenModal('space-count-warning');
    }

    if (values.venueId && values.venueId !== venue.id) {
      return setOpenModal('add-to-booking-failure');
    }

    if (!values.spaceIds.includes(space.id)) {
      setFieldValue('date', date);
      setFieldValue('venueId', venue.id);
      setFieldValue('spaceIds', [...values.spaceIds, space.id]);
    }

    return null;
  };

  const value = useMemo(
    () => ({
      [date]: [...spaces]
        .sort(compareSpaces)
        .map(({ id }) => ({
          [`s${id}`]: values.spaceIds.includes(id) ? values.slots : [],
        }))
        .reduce((prev, curr) => ({ ...prev, ...curr }), {}),
    }),
    [date, spaces, values.slots, values.spaceIds]
  );

  // The calendar hands back its date-keyed, space-keyed slot record.
  type TimeSlot = { startTime: string; endTime: string };
  type CalendarValue = Record<string, Record<string, TimeSlot[]>>;

  // Takes `object` to match Calendar's setter prop; the calendar always
  // supplies the record shape above.
  const setValue = (nextValueRaw: object) => {
    const nextValue = nextValueRaw as CalendarValue;
    const eq = (a: TimeSlot) => (b: TimeSlot) =>
      a.startTime === b.startTime && a.endTime === b.endTime;
    const unique = (item: TimeSlot, index: number, list: TimeSlot[]) =>
      list.findIndex(eq(item)) === index;

    const spaceInBooking = Object.values(nextValue)
      .flatMap(row =>
        Object.keys(row)
          .filter(key => row[key].length > 0)
          .map(key => values.spaceIds.some((id: number) => key === `s${id}`))
      )
      .reduce((prev, curr) => prev && curr, true);

    if (!spaceInBooking) return setOpenModal('no-space-error');
    if (!values.service) return setOpenModal('no-service-error');

    const getRowSlots = (row: Record<string, TimeSlot[]>) =>
      Object.keys(row)
        .filter(key => values.spaceIds.some((id: number) => key === `s${id}`))
        .flatMap(key => row[key]);

    const previousSlots = Object.values(value).flatMap(getRowSlots);
    const nextSlots = Object.values(nextValue).flatMap(getRowSlots);

    return setFieldValue(
      'slots',
      nextSlots
        // Filter out deselected slots.
        .filter(
          slot =>
            nextSlots.filter(eq(slot)).length >=
            previousSlots.filter(eq(slot)).length
        )
        .filter(unique)
    );
  };

  const calendarAvailability = useMemo(
    () => getCalendarAvailability(value, [availability] as never[]),
    [value, availability]
  );

  const SpaceDetailsComponent = ({
    space = {} as ComponentProps<typeof SpaceDetails>['space'],
    ...detailsProps
  }) => {
    const serviceClash = spaceServiceClash(space);
    const typeValid = spaceTypeValid(space);

    return (
      <SpaceDetails
        venue={venue}
        space={space}
        disabled={serviceClash || !typeValid}
        onAddToBooking={() =>
          addToBooking(space, venue, serviceClash, typeValid)
        }
        addedToBooking={values.spaceIds.includes(space.id)}
        {...detailsProps}
      />
    );
  };

  const HeaderComponent: typeof Header = props => (
    <Header {...props} date={date} setDate={setDate} />
  );

  return (
    <FormikProvider value={formik}>
      <Form>
        <Calendar
          value={value}
          setValue={setValue}
          clinical={clinical}
          availability={calendarAvailability}
          venue={venue}
          bookingItems={bookingItems}
          slots={bookingSlots}
          spaces={spaces}
          openingTime={openingTime}
          closingTime={closingTime}
          spaceDetailsComponent={
            SpaceDetailsComponent as ComponentProps<
              typeof Calendar
            >['spaceDetailsComponent']
          }
          headerComponent={HeaderComponent}
          loading={loading}
          {...props}
        />
        <SpaceListModals openModal={openModal} onClose={closeModal} />
      </Form>
    </FormikProvider>
  );
};

export default VenueCalendar;
